import {
  BilyAccountContext,
  BilyUser,
  trackAppEvent,
  trackSignedInUser,
} from '@/components/bily-tracking';
import { useAuth, useOrganization, useUser } from '@clerk/nextjs';
import { useCallback, useEffect, useMemo, useRef } from 'react';

/**
 * Builds the Bily customer identity from authoritative Clerk session state.
 * Returns null until Clerk has actually confirmed a signed-in user, so callers
 * cannot accidentally emit a conversion for an anonymous visitor.
 */
export function useBilyCustomer(): { user: BilyUser; account: BilyAccountContext } | null {
  const { isLoaded: isUserLoaded, isSignedIn, user } = useUser();
  const { organization } = useOrganization();
  const { orgId } = useAuth();

  const userId = isUserLoaded && isSignedIn ? user?.id : undefined;
  const email = user?.primaryEmailAddress?.emailAddress;
  // Sign-up collects the phone into unsafeMetadata; Clerk only holds a
  // primaryPhoneNumber when the number was verified through Clerk itself.
  const phone =
    user?.primaryPhoneNumber?.phoneNumber ??
    ((user?.unsafeMetadata as Record<string, unknown>)?.phone_number as string | undefined);
  const firstName = user?.firstName ?? undefined;
  const lastName = user?.lastName ?? undefined;
  const createdAt = user?.createdAt?.toISOString();
  const organizationId = organization?.id ?? orgId ?? undefined;
  const organizationName = organization?.name;

  // Memoized on primitives so consumers can safely use this in effect deps.
  return useMemo(
    () =>
      userId
        ? {
            user: { id: userId, email, phone, firstName, lastName },
            account: {
              organizationId,
              organizationName,
              accountCreatedAt: createdAt,
            },
          }
        : null,
    [userId, email, phone, firstName, lastName, organizationId, organizationName, createdAt]
  );
}

/**
 * Emits Bily lifecycle events from verified Clerk session state:
 *   - `Login` once per Clerk session, i.e. after authentication actually
 *     succeeded. This covers the email/password, Google OAuth and invitation
 *     ticket paths from one seam, so no sign-in button needs its own call.
 *   - `workspace_switched` when the active organization changes, which
 *     refreshes the customer context Bily holds for later events.
 *
 * Call once at the app root, inside `<ClerkProvider>`.
 */
export function useBilyIdentify() {
  const { sessionId } = useAuth();
  const customer = useBilyCustomer();

  const identifiedSessionRef = useRef<string | null>(null);
  const lastOrgRef = useRef<string | null>(null);

  useEffect(() => {
    if (!customer || !sessionId) return;

    // One Login per Clerk session — not per page load, and never from a click.
    if (identifiedSessionRef.current !== sessionId) {
      identifiedSessionRef.current = sessionId;
      lastOrgRef.current = customer.account.organizationId ?? null;
      trackSignedInUser(customer.user, customer.account);
      return;
    }

    // Verified workspace switch inside an established session.
    const orgId = customer.account.organizationId ?? null;
    if (orgId && orgId !== lastOrgRef.current) {
      lastOrgRef.current = orgId;
      trackAppEvent('workspace_switched', {
        bily_business_event: 'Workspace Switched',
        bily_funnel_stage: 'retention',
        client: { userId: customer.user.id, userGroup: orgId },
        user_id: customer.user.id,
        organization_id: orgId,
        organization_name: customer.account.organizationName,
        workspace_id: orgId,
      });
    }
  }, [sessionId, customer]);
}

/**
 * Returns a reporter for user-visible product failures. Carries the failure
 * point only — never the user's input, tokens, or payment details.
 */
export function useBilyErrorReporter() {
  return useCallback(
    (context: string, reason: string, properties: Record<string, unknown> = {}) => {
      trackAppEvent('product_error_encountered', {
        bily_business_event: 'Product Error Encountered',
        error_context: context,
        error_reason: reason,
        ...properties,
      });
    },
    []
  );
}
